import {
  ConfigApi,
  createApiRef,
  OAuthApi,
  OpenIdConnectApi,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
} from '@backstage/core-plugin-api';
import {
  GetIncidentsRequest as SchemaGetIncidentsRequest,
  GetIncidentsResponse,
  GenerateUserPRReportRequest as SchemaGenerateUserPRReportRequest,
  GenerateUserPRReportResponse,
  GenerateCodeHealthReportRequest as SchemaGenerateCodeHealthReportRequest,
  GenerateCodeHealthReportResponse,
  GeneratePRReportRequest as SchemaGeneratePRReportRequest,
  GeneratePRReportResponse,
  GenerateRepoReportRequest as SchemaGenerateRepoReportRequest,
  GenerateRepoReportResponse,
  GenerateFrontendHealthReportRequest as SchemaGenerateFrontendHealthReportRequest,
  GenerateFrontendHealthReportResponse,
  CoverageByType as SchemaCoverageByType,
  RepoSummaryWithTypes as SchemaRepoSummaryWithTypes,
  TestTypeCoverage as SchemaTestTypeCoverage,
  PullRequestWithTypedCoverage as SchemaPullRequestWithTypedCoverage,
  PRCoverageDelta as SchemaPRCoverageDelta,
  RepoHistoryWithTypes as SchemaRepoHistoryWithTypes,
  FrontendCoverageByType as SchemaFrontendCoverageByType,
  FrontendProjectSummary as SchemaFrontendProjectSummary,
  FrontendCoverageMetrics as SchemaFrontendCoverageMetrics,
  FrontendCoverageStat as SchemaFrontendCoverageStat,
} from '@weave/schema-gen-ts/dist/schemas/team-metrics/api.pb';

export interface AddRepositoryExclusionsRequest {
  repoNames: string[];
  reason?: string;
}

export interface RemoveRepositoryExclusionsRequest {
  repoNames: string[];
}

// Re-export schema request types for external use
export type GenerateUserPRReportRequest = SchemaGenerateUserPRReportRequest;
export type GetIncidentsRequest = SchemaGetIncidentsRequest;
export type GenerateCodeHealthReportRequest = SchemaGenerateCodeHealthReportRequest;
export type GeneratePRReportRequest = SchemaGeneratePRReportRequest;
export type GenerateRepoReportRequest = SchemaGenerateRepoReportRequest;
export type GenerateFrontendHealthReportRequest = SchemaGenerateFrontendHealthReportRequest;
export type { GetIncidentsResponse };

// Re-export schema types needed by components
export type { SchemaCoverageByType as CoverageByType };
export type { SchemaTestTypeCoverage as TestTypeCoverage };
export type { SchemaPRCoverageDelta as PRCoverageDelta };

// UI response types (transformed from schema types for frontend consumption)
export interface PRMetrics {
  id: string;
  title: string;
  author: string;
  weaveUser: string;
  service: string;
  coverageDelta: string | null;
  date: string;
  additions: number;
  deletions: number;
  prUrl: string;
  status: 'merged' | 'open' | 'closed';
}

export interface UITeamPRsResponse {
  prs: PRMetrics[];
  totalCount: number;
  avgDelta: string;
}


// Incident-related UI types
export interface LabeledCount {
  label: string;
  count: number;
}

export interface UIThirdPartyBreakdown {
  thirdParty: number;
  internal: number;
  unknown: number;
}

export interface UIIncidentHistory {
  date: string;
  incidentCount: number | null;
  totalDurationMinutes: number | null;
  mttrMinutes: number | null;
  totalSupportCalls: number | null;
  severityDistribution: LabeledCount[];
  criticalityDistribution: LabeledCount[];
  productImpactDistribution: LabeledCount[];
  thirdPartyBreakdown: UIThirdPartyBreakdown;
}

export interface UIIncident {
  fields: Record<string, string>;
  spreadsheetRowLink: string;
}

export interface UIIncidentSummary {
  totalIncidents: number;
  mttrMinutes: number | null;
  totalSupportCalls: number | null;
  severityDistribution: LabeledCount[];
  criticalityDistribution: LabeledCount[];
  productImpactDistribution: LabeledCount[];
  thirdPartyBreakdown: UIThirdPartyBreakdown;
  incidentHistory: UIIncidentHistory[];
  incidents: UIIncident[];
  incidentColumnHeaders: string[];
}

// Code Health Report types (multi-team, multi-test-type)
export interface UICoverageByType {
  testType: string;
  averageCoverage: number;
  averageDelta: number;
  reposIncluded: string[];
}

export interface UITestTypeCoverage {
  testType: string;
  coveragePercentage: number | null;
  coverageDelta: number | null;
  coveredUnits: number | null;
  totalUnits: number | null;
}

export interface UIRepoWithTypedCoverage {
  name: string;
  owningTeam: string;
  coverage: UITestTypeCoverage[];
  totalUnits: number | null;
  prCount: number;
  deploymentCount: number;
  isExcluded: boolean;
  exclusionReason: string | null;
  excludedByEmail: string | null;
  excludedAt: string | null;
  color: string;
}

export interface UICodeHealthResponse {
  teamCoverageByType: UICoverageByType[];
  orgCoverageByType: UICoverageByType[];
  repoSummaries: UIRepoWithTypedCoverage[];
}

export interface UIPRCoverageDelta {
  testType: string;
  delta: number | null;
  baseTotalUnits: number | null;
  baseCoveredUnits: number | null;
  headTotalUnits: number | null;
  headCoveredUnits: number | null;
}

export interface UIPRWithTypedCoverage {
  id: string;
  title: string;
  author: string;
  weaveUser: string;
  service: string;
  coverageDeltas: UIPRCoverageDelta[];
  date: string;
  additions: number;
  deletions: number;
  prUrl: string;
  status: 'merged' | 'open' | 'closed';
  repoIsExcluded: boolean;
}

export interface UITypedPRsResponse {
  prs: UIPRWithTypedCoverage[];
  totalCount: number;
  avgDeltaByType: UICoverageByType[];
}

// Repo Report types (single repo with multi-test-type support)
export interface UIRepoCoverageHistoryEntry {
  date: string;
  coverageByType: UITestTypeCoverage[];
}

export interface UIRepoReportResponse {
  repoName: string;
  coverageByType: UITestTypeCoverage[];
  orgCoverageByType: UICoverageByType[];
  coverageHistory: UIRepoCoverageHistoryEntry[];
  totalUnits: number | null;
  deploymentCount: number;
  prs: UIPRWithTypedCoverage[];
  isExcluded: boolean;
  exclusionReason: string | null;
  excludedByEmail: string | null;
  excludedAt: string | null;
}

// Frontend Health Report types
export interface UIFrontendCoverageStat {
  total: number;
  covered: number;
  pct: number | null;
}

export interface UIFrontendCoverageMetrics {
  lines: UIFrontendCoverageStat;
  statements: UIFrontendCoverageStat;
  branches: UIFrontendCoverageStat;
  functions: UIFrontendCoverageStat;
}

export interface UIFrontendCoverageByType {
  testType: string;
  metrics: UIFrontendCoverageMetrics;
  statementsCoverageDelta: number | null;
}

export interface UIFrontendProjectSummary {
  projectName: string;
  coverageByType: UIFrontendCoverageByType[];
  totalLines: number | null;
  githubTeamOwners: string[];
  isExcluded: boolean;
  exclusionReason: string | null;
  excludedByEmail: string | null;
  excludedAt: string | null;
}

export interface UIFrontendHealthResponse {
  aggregateCoverageByType: UIFrontendCoverageByType[];
  projectSummaries: UIFrontendProjectSummary[];
  deploymentCount: number | null;
  pullRequests: UIPRWithTypedCoverage[];
}

// Legacy exports for backwards compatibility
export type TeamPRsResponse = UITeamPRsResponse;

// API Interface
export interface TeamMetricsApi {
  GenerateUserPRReport(request: GenerateUserPRReportRequest): Promise<UITeamPRsResponse>;
  AddRepositoryExclusions(request: AddRepositoryExclusionsRequest): Promise<void>;
  RemoveRepositoryExclusions(request: RemoveRepositoryExclusionsRequest): Promise<void>;
  GetIncidents(request: GetIncidentsRequest): Promise<GetIncidentsResponse>;
  GenerateCodeHealthReport(request: GenerateCodeHealthReportRequest): Promise<UICodeHealthResponse>;
  GeneratePRReport(request: GeneratePRReportRequest): Promise<UITypedPRsResponse>;
  GenerateRepoReport(request: GenerateRepoReportRequest): Promise<UIRepoReportResponse>;
  GenerateFrontendHealthReport(request: GenerateFrontendHealthReportRequest): Promise<UIFrontendHealthResponse>;
}

// Helper functions for transformations
const SERVICE_COLORS = ['#3182CE', '#38A169', '#805AD5', '#D69E2E', '#E53E3E', '#00B5D8'];

export function getStatus(coverage: number | null | undefined): 'healthy' | 'warning' | 'critical' | 'unknown' {
  if (coverage == null) return 'unknown';
  if (coverage >= 50) return 'healthy';
  if (coverage >= 25) return 'warning';
  return 'critical';
}

function formatDelta(delta: number): string {
  const formatted = delta.toFixed(2);
  return delta >= 0 ? `+${formatted}%` : `${formatted}%`;
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toISOString().split('T')[0];
}

function formatMonthDate(isoString: string): string {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
}

function toRFC3339(dateString: string | undefined): string | undefined {
  if (!dateString) return undefined;
  // If already in RFC 3339 format, return as-is
  if (dateString.includes('T')) return dateString;
  // Convert YYYY-MM-DD to RFC 3339 timestamp
  return `${dateString}T00:00:00Z`;
}

function extractPRNumberFromLink(githubLink: string | undefined): string {
  if (!githubLink) return 'PR';
  const match = githubLink.match(/\/pull\/(\d+)/);
  return match ? `#${match[1]}` : 'PR';
}

function transformTypedPullRequestToLegacy(pr: SchemaPullRequestWithTypedCoverage): PRMetrics {
  const combinedDelta = (pr.coverageDeltas ?? []).find(
    (d) => (d.testType ?? '').toLowerCase() === 'go_combined'
  );
  return {
    id: extractPRNumberFromLink(pr.githubPrLink),
    title: pr.title ?? '',
    author: pr.weaveUser ? pr.weaveUser.split('@')[0] : pr.githubUser ?? '',
    weaveUser: pr.weaveUser ?? '',
    service: pr.repoName ?? '',
    coverageDelta: combinedDelta?.delta != null ? formatDelta(combinedDelta.delta) : null,
    date: formatDate(pr.mergedAt ?? ''),
    additions: pr.linesAdded ?? 0,
    deletions: pr.linesRemoved ?? 0,
    prUrl: pr.githubPrLink ?? '',
    status: 'merged' as const,
  };
}

export function formatMttrMinutes(minutes: number | null): string {
  if (minutes === null) return 'N/A';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
}

export const teamMetricsApiRef = createApiRef<TeamMetricsApi>({
  id: 'plugin.team-metrics-api.service',
});

export class TeamMetricsApiClient implements TeamMetricsApi {
  private readonly baseURL: string;
  private readonly oktaApi: OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi;

  constructor(
    configApi: ConfigApi,
    oktaAuthApi: OAuthApi & OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
  ) {
    const baseUrlConfig = configApi.getOptionalString('app.teamMetricsApiBaseUrl');
    this.baseURL = baseUrlConfig || 'https://api.weaveconnect.com';
    this.oktaApi = oktaAuthApi;
  }

  private async handleErrors(response: Response) {
    if (!response.ok) {
      let error = await response.json();
      throw new TeamMetricsApiError({
        code: error.code || response.status,
        message: error.message || response.statusText,
      });
    }
    return response;
  }

  private async fetch<T extends any>(uri: string, requestInit: RequestInit): Promise<T> {
    const idToken = await this.oktaApi.getIdToken();

    requestInit.headers = {
      ...requestInit.headers,
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };

    return fetch(`${this.baseURL}${uri}`, requestInit)
      .then(this.handleErrors)
      .then<T>((res) => res.json());
  }

  async GenerateUserPRReport(request: GenerateUserPRReportRequest): Promise<UITeamPRsResponse> {
    const body: SchemaGenerateUserPRReportRequest = {
      usernames: request.usernames,
      startDate: request.startDate ? toRFC3339(request.startDate) : undefined,
    };

    const response = await this.fetch<GenerateUserPRReportResponse>(
      '/team-metrics/v1/reports/user-pull-requests',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    const prs: PRMetrics[] = (response.pullRequests ?? []).map(
      (pr: SchemaPullRequestWithTypedCoverage) => transformTypedPullRequestToLegacy(pr)
    );

    const prsWithDelta = prs.filter((pr) => pr.coverageDelta !== null);
    const totalDelta = prsWithDelta.reduce((acc, pr) => {
      const delta = parseFloat(pr.coverageDelta!.replace('%', '').replace('+', ''));
      return acc + delta;
    }, 0);
    const avgDelta = prsWithDelta.length > 0 ? totalDelta / prsWithDelta.length : 0;

    return {
      prs,
      totalCount: prs.length,
      avgDelta: formatDelta(avgDelta),
    };
  }

  async AddRepositoryExclusions(request: AddRepositoryExclusionsRequest): Promise<void> {
    await this.fetch<Record<string, never>>(`/team-metrics/v1/repos/exclude`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async RemoveRepositoryExclusions(request: RemoveRepositoryExclusionsRequest): Promise<void> {
    await this.fetch<Record<string, never>>(`/team-metrics/v1/repos/include`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async GetIncidents(request: GetIncidentsRequest): Promise<GetIncidentsResponse> {
    const queryParams = new URLSearchParams();
    if (request.startDate) {
      const rfc3339Date = toRFC3339(request.startDate);
      if (rfc3339Date) queryParams.append('startDate', rfc3339Date);
    }
    if (request.endDate) {
      const rfc3339Date = toRFC3339(request.endDate);
      if (rfc3339Date) queryParams.append('endDate', rfc3339Date);
    }
    const queryString = queryParams.toString();
    const url = `/team-metrics/v1/incidents${queryString ? `?${queryString}` : ''}`;

    return this.fetch<GetIncidentsResponse>(url, { method: 'GET' });
  }

  async GenerateCodeHealthReport(request: GenerateCodeHealthReportRequest): Promise<UICodeHealthResponse> {
    const body: SchemaGenerateCodeHealthReportRequest = {
      teamNames: request.teamNames,
      startDate: request.startDate ? toRFC3339(request.startDate) : undefined,
    };

    const response = await this.fetch<GenerateCodeHealthReportResponse>(
      '/team-metrics/v1/reports/code-health',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    const normalizeTestType = (testType: string | undefined): string => {
      return (testType ?? '').toLowerCase();
    };

    const transformCoverageByType = (coverage: SchemaCoverageByType): UICoverageByType => ({
      testType: normalizeTestType(coverage.testType),
      averageCoverage: coverage.averageCoverage ?? 0,
      averageDelta: coverage.averageDelta ?? 0,
      reposIncluded: coverage.reposIncluded ?? [],
    });

    const parseUnits = (value: string | number | null | undefined): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
      return isNaN(parsed) ? null : parsed;
    };

    const transformTestTypeCoverage = (coverage: SchemaTestTypeCoverage): UITestTypeCoverage => ({
      testType: normalizeTestType(coverage.testType),
      coveragePercentage: coverage.coveragePercentage ?? null,
      coverageDelta: coverage.coverageDelta ?? null,
      coveredUnits: parseUnits(coverage.coveredUnits),
      totalUnits: parseUnits(coverage.totalUnits),
    });

    const repoSummaries: UIRepoWithTypedCoverage[] = (response.repoSummaries ?? [])
      .map((repo: SchemaRepoSummaryWithTypes, index: number) => ({
        name: repo.repoName ?? '',
        owningTeam: repo.owningTeam ?? '',
        coverage: (repo.coverage ?? []).map(transformTestTypeCoverage),
        totalUnits: parseUnits(repo.totalUnits),
        prCount: repo.prCount ?? 0,
        deploymentCount: repo.deploymentCount ?? 0,
        isExcluded: repo.isExcluded ?? false,
        exclusionReason: repo.exclusionReason ?? null,
        excludedByEmail: repo.excludedByEmail ?? null,
        excludedAt: repo.excludedAt ?? null,
        color: SERVICE_COLORS[index % SERVICE_COLORS.length],
      }))
      .sort((a: UIRepoWithTypedCoverage, b: UIRepoWithTypedCoverage) => {
        // Sort by PR count desc, then deploys desc, then combined coverage desc
        if (b.prCount !== a.prCount) return b.prCount - a.prCount;
        if (b.deploymentCount !== a.deploymentCount) return b.deploymentCount - a.deploymentCount;
        const aCoverage = a.coverage.find((c) => c.testType === 'go_combined')?.coveragePercentage ?? null;
        const bCoverage = b.coverage.find((c) => c.testType === 'go_combined')?.coveragePercentage ?? null;
        if (aCoverage === null && bCoverage === null) return 0;
        if (aCoverage === null) return 1;
        if (bCoverage === null) return -1;
        return bCoverage - aCoverage;
      });

    return {
      teamCoverageByType: (response.teamCoverageByType ?? []).map(transformCoverageByType),
      orgCoverageByType: (response.orgCoverageByType ?? []).map(transformCoverageByType),
      repoSummaries,
    };
  }

  async GeneratePRReport(request: GeneratePRReportRequest): Promise<UITypedPRsResponse> {
    const body: SchemaGeneratePRReportRequest = {
      teamNames: request.teamNames,
      startDate: request.startDate ? toRFC3339(request.startDate) : undefined,
      weaveUsers: request.weaveUsers,
    };

    const response = await this.fetch<GeneratePRReportResponse>(
      '/team-metrics/v1/reports/pull-requests',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    const normalizeTestType = (testType: string | undefined): string => {
      return (testType ?? '').toLowerCase();
    };

    const transformPRCoverageDelta = (delta: SchemaPRCoverageDelta): UIPRCoverageDelta => ({
      testType: normalizeTestType(delta.testType),
      delta: delta.delta ?? null,
      baseTotalUnits: delta.baseTotalUnits ?? null,
      baseCoveredUnits: delta.baseCoveredUnits ?? null,
      headTotalUnits: delta.headTotalUnits ?? null,
      headCoveredUnits: delta.headCoveredUnits ?? null,
    });

    const extractPRNumber = (githubLink: string | undefined): string => {
      if (!githubLink) return 'PR';
      const match = githubLink.match(/\/pull\/(\d+)/);
      return match ? `#${match[1]}` : 'PR';
    };

    const prs: UIPRWithTypedCoverage[] = (response.pullRequests ?? []).map(
      (pr: SchemaPullRequestWithTypedCoverage) => ({
        id: extractPRNumber(pr.githubPrLink),
        title: pr.title ?? '',
        author: pr.weaveUser ? pr.weaveUser.split('@')[0] : pr.githubUser ?? '',
        weaveUser: pr.weaveUser ?? '',
        service: pr.repoName ?? '',
        coverageDeltas: (pr.coverageDeltas ?? []).map(transformPRCoverageDelta),
        date: formatDate(pr.mergedAt ?? ''),
        additions: pr.linesAdded ?? 0,
        deletions: pr.linesRemoved ?? 0,
        prUrl: pr.githubPrLink ?? '',
        status: 'merged' as const,
        repoIsExcluded: pr.repoIsExcluded ?? false,
      })
    );

    // Calculate average delta by test type
    const testTypes = ['go_unit', 'go_tas', 'go_combined'];
    const avgDeltaByType: UICoverageByType[] = testTypes.map((testType) => {
      const prsWithType = prs.filter((pr) =>
        pr.coverageDeltas.some((d) => d.testType === testType && d.delta !== null)
      );
      const totalDelta = prsWithType.reduce((acc, pr) => {
        const delta = pr.coverageDeltas.find((d) => d.testType === testType)?.delta ?? 0;
        return acc + (delta ?? 0);
      }, 0);
      const avgDelta = prsWithType.length > 0 ? totalDelta / prsWithType.length : 0;

      return {
        testType,
        averageCoverage: 0,
        averageDelta: avgDelta,
        reposIncluded: [],
      };
    });

    return {
      prs,
      totalCount: prs.length,
      avgDeltaByType,
    };
  }

  async GenerateRepoReport(request: GenerateRepoReportRequest): Promise<UIRepoReportResponse> {
    const body: SchemaGenerateRepoReportRequest = {
      repoName: request.repoName,
      startDate: request.startDate ? toRFC3339(request.startDate) : undefined,
    };

    const response = await this.fetch<GenerateRepoReportResponse>(
      '/team-metrics/v1/reports/repo',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    const normalizeTestType = (testType: string | undefined): string => {
      return (testType ?? '').toLowerCase();
    };

    const parseUnits = (value: string | number | null | undefined): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
      return isNaN(parsed) ? null : parsed;
    };

    const transformTestTypeCoverage = (coverage: SchemaTestTypeCoverage): UITestTypeCoverage => ({
      testType: normalizeTestType(coverage.testType),
      coveragePercentage: coverage.coveragePercentage ?? null,
      coverageDelta: coverage.coverageDelta ?? null,
      coveredUnits: parseUnits(coverage.coveredUnits),
      totalUnits: parseUnits(coverage.totalUnits),
    });

    const transformCoverageByType = (coverage: SchemaCoverageByType): UICoverageByType => ({
      testType: normalizeTestType(coverage.testType),
      averageCoverage: coverage.averageCoverage ?? 0,
      averageDelta: coverage.averageDelta ?? 0,
      reposIncluded: coverage.reposIncluded ?? [],
    });

    const transformPRCoverageDelta = (delta: SchemaPRCoverageDelta): UIPRCoverageDelta => ({
      testType: normalizeTestType(delta.testType),
      delta: delta.delta ?? null,
      baseTotalUnits: parseUnits(delta.baseTotalUnits),
      baseCoveredUnits: parseUnits(delta.baseCoveredUnits),
      headTotalUnits: parseUnits(delta.headTotalUnits),
      headCoveredUnits: parseUnits(delta.headCoveredUnits),
    });

    const extractPRNumber = (githubLink: string | undefined): string => {
      if (!githubLink) return 'PR';
      const match = githubLink.match(/\/pull\/(\d+)/);
      return match ? `#${match[1]}` : 'PR';
    };

    const coverageHistory: UIRepoCoverageHistoryEntry[] = (response.coverageHistory ?? []).map(
      (entry: SchemaRepoHistoryWithTypes) => ({
        date: formatMonthDate(entry.date ?? ''),
        coverageByType: (entry.coverageByType ?? []).map(transformTestTypeCoverage),
      })
    );

    const prs: UIPRWithTypedCoverage[] = (response.pullRequests ?? []).map(
      (pr: SchemaPullRequestWithTypedCoverage) => ({
        id: extractPRNumber(pr.githubPrLink),
        title: pr.title ?? '',
        author: pr.weaveUser ? pr.weaveUser.split('@')[0] : pr.githubUser ?? '',
        weaveUser: pr.weaveUser ?? '',
        service: pr.repoName ?? '',
        coverageDeltas: (pr.coverageDeltas ?? []).map(transformPRCoverageDelta),
        date: formatDate(pr.mergedAt ?? ''),
        additions: pr.linesAdded ?? 0,
        deletions: pr.linesRemoved ?? 0,
        prUrl: pr.githubPrLink ?? '',
        status: 'merged' as const,
        repoIsExcluded: pr.repoIsExcluded ?? false,
      })
    );

    return {
      repoName: response.repoName ?? request.repoName ?? '',
      coverageByType: (response.coverageByType ?? []).map(transformTestTypeCoverage),
      orgCoverageByType: (response.orgCoverageByType ?? []).map(transformCoverageByType),
      coverageHistory,
      totalUnits: parseUnits(response.totalUnits),
      deploymentCount: response.deploymentCount ?? 0,
      prs,
      isExcluded: response.isExcluded ?? false,
      exclusionReason: response.exclusionReason ?? null,
      excludedByEmail: response.excludedByEmail ?? null,
      excludedAt: response.excludedAt ?? null,
    };
  }

  async GenerateFrontendHealthReport(request: GenerateFrontendHealthReportRequest): Promise<UIFrontendHealthResponse> {
    const body: SchemaGenerateFrontendHealthReportRequest = {
      startDate: request.startDate ? toRFC3339(request.startDate) : undefined,
    };

    const response = await this.fetch<GenerateFrontendHealthReportResponse>(
      '/team-metrics/v1/reports/frontend-health',
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );

    const parseNumber = (value: string | number | null | undefined): number => {
      if (value === null || value === undefined || value === '') return 0;
      const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
      return isNaN(parsed) ? 0 : parsed;
    };

    const parseNullableNumber = (value: string | number | null | undefined): number | null => {
      if (value === null || value === undefined || value === '') return null;
      const parsed = typeof value === 'string' ? parseFloat(String(value)) : value;
      return isNaN(parsed) ? null : parsed;
    };

    const transformCoverageStat = (stat: SchemaFrontendCoverageStat | undefined): UIFrontendCoverageStat => ({
      total: parseNumber(stat?.total),
      covered: parseNumber(stat?.covered),
      pct: parseNullableNumber(stat?.pct),
    });

    const transformCoverageMetrics = (metrics: SchemaFrontendCoverageMetrics | undefined): UIFrontendCoverageMetrics => ({
      lines: transformCoverageStat(metrics?.lines),
      statements: transformCoverageStat(metrics?.statements),
      branches: transformCoverageStat(metrics?.branches),
      functions: transformCoverageStat(metrics?.functions),
    });

    const transformFrontendCoverageByType = (coverage: SchemaFrontendCoverageByType): UIFrontendCoverageByType => ({
      testType: coverage.testType ?? '',
      metrics: transformCoverageMetrics(coverage.metrics),
      statementsCoverageDelta: parseNullableNumber(coverage.statementsCoverageDelta),
    });

    const transformProjectSummary = (project: SchemaFrontendProjectSummary): UIFrontendProjectSummary => ({
      projectName: project.projectName ?? '',
      coverageByType: (project.coverageByType ?? []).map(transformFrontendCoverageByType),
      totalLines: parseNullableNumber(project.totalStatements),
      githubTeamOwners: project.githubTeamOwners ?? [],
      isExcluded: project.isExcluded ?? false,
      exclusionReason: project.exclusionReason ?? null,
      excludedByEmail: project.excludedByEmail ?? null,
      excludedAt: project.excludedAt ?? null,
    });

    const extractPRNumber = (githubLink: string | undefined): string => {
      if (!githubLink) return 'PR';
      const match = githubLink.match(/\/pull\/(\d+)/);
      return match ? `#${match[1]}` : 'PR';
    };

    const transformPRCoverageDelta = (delta: SchemaPRCoverageDelta): UIPRCoverageDelta => ({
      testType: delta.testType ?? '',
      delta: parseNullableNumber(delta.delta),
      baseTotalUnits: parseNullableNumber(delta.baseTotalUnits),
      baseCoveredUnits: parseNullableNumber(delta.baseCoveredUnits),
      headTotalUnits: parseNullableNumber(delta.headTotalUnits),
      headCoveredUnits: parseNullableNumber(delta.headCoveredUnits),
    });

    const pullRequests: UIPRWithTypedCoverage[] = (response.pullRequests ?? []).map(
      (pr: SchemaPullRequestWithTypedCoverage) => ({
        id: extractPRNumber(pr.githubPrLink),
        title: pr.title ?? '',
        author: pr.weaveUser ? pr.weaveUser.split('@')[0] : pr.githubUser ?? '',
        weaveUser: pr.weaveUser ?? '',
        service: pr.repoName ?? '',
        coverageDeltas: (pr.coverageDeltas ?? []).map(transformPRCoverageDelta),
        date: formatDate(pr.mergedAt ?? ''),
        additions: pr.linesAdded ?? 0,
        deletions: pr.linesRemoved ?? 0,
        prUrl: pr.githubPrLink ?? '',
        status: 'merged' as const,
        repoIsExcluded: pr.repoIsExcluded ?? false,
      })
    );

    return {
      aggregateCoverageByType: (response.aggregateCoverageByType ?? []).map(transformFrontendCoverageByType),
      projectSummaries: (response.projectSummaries ?? []).map(transformProjectSummary),
      deploymentCount: parseNullableNumber(response.deploymentCount),
      pullRequests,
    };
  }
}

export class TeamMetricsApiError extends Error {
  code: number;
  message: string;

  constructor({ code, message }: { code: number; message: string }) {
    super(message);
    this.name = 'TeamMetricsApiError';
    this.code = code;
    this.message = message;
  }
}
